import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { TrendingUp, TrendingDown, Award, Clock, Target, Flame, Heart, Shield, BarChart3, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Legend
} from 'recharts';

const CATEGORY_META = {
  police: { label: 'Police', color: '#2E6CF5', icon: Shield },
  ambulance: { label: 'Ambulance', color: '#EE2130', icon: Heart },
  incendie: { label: 'Incendie', color: '#F4621C', icon: Flame },
};

const StatCard = ({ icon: Icon, label, value, unit, accent = '#002768', sub }) => (
  <Card className="border-l-4" style={{ borderLeftColor: accent }}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {value}
        {unit && <span className="text-base font-normal text-gray-500 ml-1">{unit}</span>}
      </p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-CA', { month: 'short', day: 'numeric' });
};

const Progress = ({ studentInfo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const operatorName = studentInfo
    ? `${studentInfo.first_name} ${studentInfo.last_name}`.trim()
    : '';

  useEffect(() => {
    if (!operatorName) return;
    setLoading(true);
    axios
      .get(`${API}/student/progress`, { params: { operator_name: operatorName } })
      .then((r) => setData(r.data))
      .catch((err) => console.warn('progress load failed:', err))
      .finally(() => setLoading(false));
  }, [operatorName]);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="progress-loading">
        Chargement de vos progrès...
      </div>
    );
  }

  if (!data || data.total_sessions === 0) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="progress-empty">
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-bold text-gray-700 mb-2">Aucune session encore</h2>
          <p className="text-sm text-gray-500">
            Commencez un scénario pour voir vos progrès apparaître ici.
          </p>
        </div>
      </div>
    );
  }

  const timeline = data.timeline || [];
  const chartData = timeline.map((t, i) => ({
    name: `#${i + 1}`,
    date: formatDate(t.date),
    score: t.score,
    duration: Math.round((t.duration_seconds || 0) / 60),
    category: t.category,
  }));

  const categoryData = Object.entries(data.average_by_category || {})
    .filter(([cat, _]) => CATEGORY_META[cat])
    .map(([cat, score]) => ({
      name: CATEGORY_META[cat].label,
      score,
      sessions: data.by_category[cat] || 0,
      color: CATEGORY_META[cat].color,
    }));

  const trend = data.trend_percent;
  const TrendIcon = trend !== null && trend >= 0 ? TrendingUp : TrendingDown;
  const trendColor = trend === null ? '#6b7280' : trend >= 0 ? '#16a34a' : '#dc2626';

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="progress-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Mes Progrès</h1>
        <p className="text-sm text-gray-500">
          Tableau de bord personnel de {operatorName}
        </p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Award}
          label="Score moyen"
          value={data.average_score.toFixed(1)}
          unit="/10"
          accent="#002768"
          sub={`Meilleur : ${data.best_score.toFixed(1)}/10`}
          data-testid="stat-score-moyen"
        />
        <StatCard
          icon={Target}
          label="Sessions"
          value={data.total_sessions}
          accent="#2E6CF5"
          sub={`${data.completed_sessions} complétées`}
        />
        <StatCard
          icon={Clock}
          label="Temps total"
          value={data.total_training_hours.toFixed(1)}
          unit="h"
          accent="#24B219"
          sub={`${(data.total_training_hours * 60).toFixed(0)} min`}
        />
        <StatCard
          icon={TrendIcon}
          label="Tendance récente"
          value={trend === null ? '—' : `${trend > 0 ? '+' : ''}${trend}%`}
          accent={trendColor}
          sub={trend === null ? 'Min. 6 sessions évaluées' : '5 dernières vs 5 précédentes'}
        />
      </div>

      {/* Score over time */}
      {chartData.length > 0 && (
        <Card className="mb-6" data-testid="progress-chart-timeline">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5" style={{ color: '#002768' }} />
              <h2 className="text-lg font-semibold text-gray-800">Évolution du score</h2>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                <YAxis domain={[0, 10]} stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(v, name) => name === 'score' ? `${v}/10` : `${v} min`}
                  labelFormatter={(l, payload) => {
                    const cat = payload?.[0]?.payload?.category;
                    return cat ? `${l} · ${CATEGORY_META[cat]?.label || cat}` : l;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#002768"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#002768' }}
                  activeDot={{ r: 7 }}
                  name="Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Per category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card data-testid="progress-chart-category">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Score moyen par catégorie</h2>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={categoryData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                  <YAxis domain={[0, 10]} stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(v) => `${v}/10`}
                  />
                  <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                    {categoryData.map((entry, i) => (
                      <Cell key={`cell-${entry.name}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-12">Pas encore de données</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Répartition des sessions</h2>
            <div className="space-y-4">
              {Object.entries(data.by_category || {}).map(([cat, count]) => {
                const meta = CATEGORY_META[cat];
                if (!meta) return null;
                const Icon = meta.icon;
                const pct = data.total_sessions > 0 ? (count / data.total_sessions) * 100 : 0;
                return (
                  <div key={cat} data-testid={`category-row-${cat}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: meta.color }} />
                        <span className="text-sm font-medium text-gray-700">{meta.label}</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        <strong>{count}</strong> session{count > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: meta.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Health stats */}
            {data.health_protocol_sessions > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Protocole Santé (MPDS)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center bg-red-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-red-700">{data.health_protocol_sessions}</p>
                    <p className="text-xs text-red-600 mt-1">Protocoles complétés</p>
                  </div>
                  <div className="text-center bg-orange-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-orange-700">{data.health_priority_1_count}</p>
                    <p className="text-xs text-orange-600 mt-1">Priorités 1 identifiées</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pedagogical tips */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid="progress-tips">
        <CardContent className="p-6">
          <h3 className="text-base font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Conseils personnalisés
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            {data.average_score < 7 && (
              <li>• Votre score moyen est sous 7/10 — revoyez votre <strong>3QPOC BAR</strong> à chaque appel.</li>
            )}
            {data.by_category.police === 0 && (
              <li>• Vous n'avez pas encore pratiqué de scénarios <strong>Police</strong>. Allez les essayer dans l'onglet Scénarios.</li>
            )}
            {data.by_category.ambulance === 0 && (
              <li>• Pratiquez des scénarios <strong>Ambulance</strong> pour maîtriser le protocole MPDS.</li>
            )}
            {data.by_category.incendie === 0 && (
              <li>• Aucune pratique <strong>Incendie</strong> encore. Variez vos scénarios.</li>
            )}
            {data.total_hints_used > data.total_sessions && (
              <li>• Vous utilisez beaucoup d'indices ({data.total_hints_used} pour {data.total_sessions} sessions). Essayez sans pour progresser.</li>
            )}
            {data.trend_percent !== null && data.trend_percent >= 10 && (
              <li className="text-green-800">✓ Excellente progression de {data.trend_percent}% sur vos 5 dernières sessions !</li>
            )}
            {data.average_score >= 8.5 && (
              <li className="text-green-800">✓ Score moyen excellent ({data.average_score.toFixed(1)}/10). Vous êtes prêt pour le terrain !</li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Progress;
